import { useCallback, useEffect, useMemo, useState } from "react";
import api, { apiError, downloadFile, API_ROOT } from "@/lib/api";
import { PageHeader, Loading, EmptyState, ConfirmDialog, StatusBadge } from "@/components/common";
import { fmtMoney, fmtDate, initials } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";
import { Plus, MagnifyingGlass, DotsThreeVertical, Upload, Download, CircleNotch, Key, PencilSimple, Trash, Power } from "@phosphor-icons/react";

const EMPTY = {
  full_name: "", employee_code: "", rtn: "", position: "", contract_type: "", company: "",
  monthly_salary: "", start_date: "", birthday: "", email: "", phone: "", address: "",
  bank: "", bank_account: "", role: "employee", password: "",
};

const inputCls = "rounded-sm border-input bg-background text-foreground focus-visible:ring-1 focus-visible:ring-ring focus-visible:ring-offset-0";

function photoSrc(photo) {
  if (!photo) return null;
  if (photo.startsWith("http") || photo.startsWith("data:")) return photo;
  return `${API_ROOT}${photo.startsWith("/") ? "" : "/"}${photo}`;
}

export default function Empleados() {
  const [employees, setEmployees] = useState(null);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [bulkConfirm, setBulkConfirm] = useState(false);
  const [creds, setCreds] = useState(null);
  const [importing, setImporting] = useState(false);

  const load = useCallback(() => {
    api.get("/employees").then((r) => setEmployees(Array.isArray(r.data) ? r.data : [])).catch((e) => {
      toast.error(apiError(e));
      setEmployees([]);
    });
  }, []);

  useEffect(load, [load]);

  const filtered = useMemo(() => {
    if (!employees) return [];
    const q = search.toLowerCase();
    return employees.filter((e) =>
      [e.full_name, e.employee_code, e.position, e.rtn].join(" ").toLowerCase().includes(q)
    );
  }, [employees, search]);

  const openCreate = () => {
    setEditing(null);
    setForm(EMPTY);
    setDialogOpen(true);
  };

  const openEdit = (emp) => {
    setEditing(emp);
    setForm({
      full_name: emp.full_name || "", employee_code: emp.employee_code || "", rtn: emp.rtn || "",
      position: emp.position || "", contract_type: emp.contract_type || "", company: emp.company || "",
      monthly_salary: emp.monthly_salary ?? "", start_date: emp.start_date || "", birthday: emp.birthday || "",
      email: emp.email || "", phone: emp.phone || "", address: emp.address || "",
      bank: emp.bank || "", bank_account: emp.bank_account || "", role: emp.role || "employee", password: "",
    });
    setDialogOpen(true);
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, monthly_salary: Number(form.monthly_salary) || 0 };
    if (!payload.password) delete payload.password;
    try {
      if (editing) {
        await api.patch(`/employees/${editing.id}`, payload);
        toast.success("Empleado actualizado");
      } else {
        await api.post("/employees", payload);
        toast.success("Empleado creado");
      }
      setDialogOpen(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/employees/${toDelete.id}`);
      toast.success("Empleado eliminado");
      setToDelete(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const bulkRemove = async () => {
    try {
      await api.post("/employees/bulk-delete", { ids: selected });
      toast.success(`${selected.length} empleados eliminados`);
      setSelected([]);
      setBulkConfirm(false);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleActive = async (emp) => {
    try {
      await api.patch(`/employees/${emp.id}/active`, { active: !emp.active });
      toast.success(emp.active ? "Empleado desactivado" : "Empleado activado");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const showCreds = async (emp) => {
    try {
      let r;
      try {
        r = await api.get(`/employees/${emp.id}/credentials-full`);
      } catch {
        r = await api.get(`/employees/${emp.id}/credentials`);
      }
      setCreds({ name: emp.full_name, ...r.data });
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const importFile = async (ev) => {
    const file = ev.target.files?.[0];
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/reports/import-employees", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Importación: ${data.created} creados, ${data.skipped} omitidos`);
      load();
    } catch (err) {
      toast.error(apiError(err, "No se pudo importar el archivo."));
    } finally {
      setImporting(false);
      ev.target.value = "";
    }
  };

  const toggleSelect = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));

  if (!employees) return <Loading label="Cargando empleados..." />;

  return (
    <div data-testid="empleados-page">
      <PageHeader title="Empleados" description={`${employees.length} colaboradores registrados`} testid="empleados-header">
        <Button variant="outline" className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground"
          data-testid="download-template-button"
          onClick={() => downloadFile("/reports/template-employees.xlsx", "plantilla-empleados.xlsx").catch((e) => toast.error(apiError(e)))}>
          <Download className="mr-2" size={15} /> Plantilla
        </Button>
        <label className="inline-flex">
          <input type="file" accept=".xlsx" className="hidden" data-testid="import-file-input" onChange={importFile} />
          <Button variant="outline" asChild disabled={importing}
            className="rounded-sm border-input bg-transparent text-foreground/80 hover:bg-accent hover:text-accent-foreground cursor-pointer">
            <span data-testid="import-button">
              {importing ? <CircleNotch className="mr-2 animate-spin" size={15} /> : <Upload className="mr-2" size={15} />}
              Importar
            </span>
          </Button>
        </label>
        {selected.length > 0 && (
          <Button variant="outline" className="rounded-sm border-red-900 text-red-400 hover:bg-red-950/40 hover:text-red-300"
            data-testid="bulk-delete-button" onClick={() => setBulkConfirm(true)}>
            <Trash className="mr-2" size={15} /> Eliminar ({selected.length})
          </Button>
        )}
        <Button className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="new-employee-button" onClick={openCreate}>
          <Plus className="mr-2" size={15} /> Nuevo empleado
        </Button>
      </PageHeader>

      <div className="relative mb-4 max-w-sm">
        <MagnifyingGlass className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" size={15} />
        <Input placeholder="Buscar por nombre, código, puesto..." className={`${inputCls} pl-9`} data-testid="employee-search-input"
          value={search} onChange={(e) => setSearch(e.target.value)} />
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="Sin empleados" description="Crea tu primer empleado o importa la plantilla de Excel." />
      ) : (
        <div className="border border-border rounded-sm overflow-x-auto bg-card">
          <table className="w-full text-sm" data-testid="employees-table">
            <thead>
              <tr className="text-left text-xs uppercase tracking-widest text-muted-foreground border-b border-border">
                <th className="p-3 w-10"></th>
                <th className="p-3 font-normal">Empleado</th>
                <th className="p-3 font-normal">Código</th>
                <th className="p-3 font-normal">Puesto</th>
                <th className="p-3 font-normal">Salario</th>
                <th className="p-3 font-normal">Ingreso</th>
                <th className="p-3 font-normal">Estado</th>
                <th className="p-3 font-normal text-right">Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map((emp) => (
                <tr key={emp.id} className="hover:bg-muted/70 transition-colors duration-200" data-testid={`employee-row-${emp.employee_code}`}>
                  <td className="p-3">
                    <Checkbox checked={selected.includes(emp.id)} onCheckedChange={() => toggleSelect(emp.id)}
                      data-testid={`select-employee-${emp.employee_code}`} className="border-input" />
                  </td>
                  <td className="p-3">
                    <div className="flex items-center gap-3">
                      {photoSrc(emp.profile_photo) ? (
                        <img src={photoSrc(emp.profile_photo)} alt="" className="h-8 w-8 rounded-sm object-cover border border-border" />
                      ) : (
                        <div className="h-8 w-8 rounded-sm bg-muted flex items-center justify-center text-xs text-foreground/80 font-medium">
                          {initials(emp.full_name)}
                        </div>
                      )}
                      <div>
                        <p className="text-foreground font-medium">{emp.full_name}</p>
                        <p className="text-xs text-muted-foreground">{emp.email || emp.username}</p>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 text-foreground/80">{emp.employee_code}</td>
                  <td className="p-3 text-foreground/80">{emp.position || "—"}</td>
                  <td className="p-3 text-foreground/80">{fmtMoney(emp.monthly_salary)}</td>
                  <td className="p-3 text-foreground/80">{fmtDate(emp.start_date)}</td>
                  <td className="p-3"><StatusBadge status={emp.active ? "active" : "inactive"} /></td>
                  <td className="p-3 text-right">
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8 text-muted-foreground hover:text-foreground hover:bg-accent" data-testid={`employee-actions-${emp.employee_code}`}>
                          <DotsThreeVertical size={18} />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="bg-card border-border rounded-sm">
                        <DropdownMenuItem className="text-foreground/80 focus:bg-accent focus:text-foreground cursor-pointer" data-testid={`employee-creds-${emp.employee_code}`} onClick={() => showCreds(emp)}>
                          <Key className="mr-2" size={15} /> Credenciales
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-foreground/80 focus:bg-accent focus:text-foreground cursor-pointer" data-testid={`employee-edit-${emp.employee_code}`} onClick={() => openEdit(emp)}>
                          <PencilSimple className="mr-2" size={15} /> Editar
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-foreground/80 focus:bg-accent focus:text-foreground cursor-pointer" data-testid={`employee-toggle-${emp.employee_code}`} onClick={() => toggleActive(emp)}>
                          <Power className="mr-2" size={15} /> {emp.active ? "Desactivar" : "Activar"}
                        </DropdownMenuItem>
                        <DropdownMenuItem className="text-red-400 focus:bg-red-950/40 focus:text-red-300 cursor-pointer" data-testid={`employee-delete-${emp.employee_code}`} onClick={() => setToDelete(emp)}>
                          <Trash className="mr-2" size={15} /> Eliminar
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">{editing ? "Editar empleado" : "Nuevo empleado"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={save} className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="employee-form">
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Nombre completo *</Label>
              <Input className={inputCls} required data-testid="form-full-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Código *</Label>
              <Input className={inputCls} required data-testid="form-code" value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">RTN / Identidad *</Label>
              <Input className={inputCls} required data-testid="form-rtn" value={form.rtn} onChange={(e) => setForm({ ...form, rtn: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Puesto</Label>
              <Input className={inputCls} data-testid="form-position" value={form.position} onChange={(e) => setForm({ ...form, position: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Tipo de contrato</Label>
              <Input className={inputCls} value={form.contract_type} onChange={(e) => setForm({ ...form, contract_type: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Salario mensual</Label>
              <Input type="number" step="0.01" min="0" className={inputCls} data-testid="form-salary" value={form.monthly_salary} onChange={(e) => setForm({ ...form, monthly_salary: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Fecha de ingreso</Label>
              <Input type="date" className={inputCls} value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Cumpleaños</Label>
              <Input type="date" className={inputCls} value={form.birthday} onChange={(e) => setForm({ ...form, birthday: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Correo</Label>
              <Input type="email" className={inputCls} data-testid="form-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Teléfono</Label>
              <Input className={inputCls} value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Banco</Label>
              <Input className={inputCls} value={form.bank} onChange={(e) => setForm({ ...form, bank: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Cuenta bancaria</Label>
              <Input className={inputCls} value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })} />
            </div>
            <div className="space-y-1.5 sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Dirección</Label>
              <Input className={inputCls} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-widest text-muted-foreground">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className={inputCls} data-testid="form-role"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-card border-border">
                  <SelectItem value="employee">Empleado</SelectItem>
                  <SelectItem value="admin">Administrador</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!editing && (
              <div className="space-y-1.5">
                <Label className="text-xs uppercase tracking-widest text-muted-foreground">Contraseña (opcional)</Label>
                <Input type="text" className={inputCls} placeholder="Por defecto: 1234" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <DialogFooter className="sm:col-span-2 mt-2">
              <Button type="submit" disabled={saving} className="rounded-sm bg-primary text-primary-foreground hover:bg-primary/80" data-testid="employee-save-button">
                {saving && <CircleNotch className="animate-spin mr-2" size={15} />}
                {editing ? "Guardar cambios" : "Crear empleado"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={!!creds} onOpenChange={() => setCreds(null)}>
        <DialogContent aria-describedby={undefined} className="bg-card border-border rounded-sm sm:max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-heading text-foreground">Credenciales de acceso</DialogTitle>
          </DialogHeader>
          {creds && (
            <div className="space-y-3" data-testid="credentials-dialog">
              <p className="text-sm text-muted-foreground">{creds.name}</p>
              <div className="border border-border rounded-sm divide-y divide-border text-sm">
                <div className="flex justify-between p-3"><span className="text-muted-foreground">Usuario</span><span className="text-foreground font-mono" data-testid="creds-username">{creds.username}</span></div>
                <div className="flex justify-between p-3"><span className="text-muted-foreground">Contraseña</span><span className="text-foreground font-mono" data-testid="creds-password">{creds.current_password ?? creds.default_password ?? "—"}</span></div>
              </div>
              <p className="text-xs text-muted-foreground">El empleado puede cambiarla desde la app móvil.</p>
            </div>
          )}
        </DialogContent>
      </Dialog>

      <ConfirmDialog open={!!toDelete} onOpenChange={() => setToDelete(null)}
        title="Eliminar empleado" description={`Se eliminará a ${toDelete?.full_name}. Esta acción no se puede deshacer.`}
        onConfirm={remove} testid="delete-employee" />
      <ConfirmDialog open={bulkConfirm} onOpenChange={setBulkConfirm}
        title="Eliminación masiva" description={`Se eliminarán ${selected.length} empleados seleccionados.`}
        onConfirm={bulkRemove} testid="bulk-delete" />
    </div>
  );
}
